import { useEffect, useMemo, useState } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { IconSteuer } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatEuro, parseEuroEingabe } from '../lib/format';

interface Position {
  kostenartCode: string | null;
  bezeichnung: string;
  betragCent: number;
}
interface ObjektZeile {
  objektId: string;
  bezeichnung: string;
  einnahmenKaltmieteCent: number;
  einnahmenUmlagenCent: number;
  einnahmenGesamtCent: number;
  werbungskostenCent: number;
  positionen: Position[];
  ueberschussVorAfaCent: number;
}
interface Antwort {
  jahr: number;
  objekte: ObjektZeile[];
  summe: {
    einnahmenKaltmieteCent: number;
    einnahmenUmlagenCent: number;
    einnahmenGesamtCent: number;
    werbungskostenCent: number;
    ueberschussVorAfaCent: number;
    objekteAnzahl: number;
  };
}

const JETZT = new Date().getUTCFullYear();

function demoAntwort(jahr: number): Antwort {
  const objekte: ObjektZeile[] = [
    {
      objektId: 'obj-1',
      bezeichnung: 'Lindenstraße 14',
      einnahmenKaltmieteCent: 5_712_00,
      einnahmenUmlagenCent: 1_920_00,
      einnahmenGesamtCent: 7_632_00,
      werbungskostenCent: 2_460_00,
      positionen: [
        { kostenartCode: 'grundsteuer', bezeichnung: 'Grundsteuer', betragCent: 940_00 },
        { kostenartCode: 'versicherung', bezeichnung: 'Sach- und Haftpflichtversicherung', betragCent: 620_00 },
        { kostenartCode: 'instandhaltung', bezeichnung: 'Instandhaltung / Reparatur', betragCent: 900_00 },
      ],
      ueberschussVorAfaCent: 5_172_00,
    },
    {
      objektId: 'obj-2',
      bezeichnung: 'Am Wasserturm 3',
      einnahmenKaltmieteCent: 3_240_00,
      einnahmenUmlagenCent: 1_080_00,
      einnahmenGesamtCent: 4_320_00,
      werbungskostenCent: 1_180_00,
      positionen: [
        { kostenartCode: 'grundsteuer', bezeichnung: 'Grundsteuer', betragCent: 520_00 },
        { kostenartCode: 'hausmeister', bezeichnung: 'Hausmeister', betragCent: 660_00 },
      ],
      ueberschussVorAfaCent: 3_140_00,
    },
  ];
  const summe = objekte.reduce(
    (a, o) => ({
      einnahmenKaltmieteCent: a.einnahmenKaltmieteCent + o.einnahmenKaltmieteCent,
      einnahmenUmlagenCent: a.einnahmenUmlagenCent + o.einnahmenUmlagenCent,
      einnahmenGesamtCent: a.einnahmenGesamtCent + o.einnahmenGesamtCent,
      werbungskostenCent: a.werbungskostenCent + o.werbungskostenCent,
      ueberschussVorAfaCent: a.ueberschussVorAfaCent + o.ueberschussVorAfaCent,
      objekteAnzahl: 0,
    }),
    {
      einnahmenKaltmieteCent: 0,
      einnahmenUmlagenCent: 0,
      einnahmenGesamtCent: 0,
      werbungskostenCent: 0,
      ueberschussVorAfaCent: 0,
      objekteAnzahl: 0,
    },
  );
  summe.objekteAnzahl = objekte.length;
  return { jahr, objekte, summe };
}

export function Steuer() {
  const [jahr, setJahr] = useState(JETZT);
  const [daten, setDaten] = useState<Antwort | null>(DEMO ? demoAntwort(JETZT) : null);
  const [afa, setAfa] = useState<Record<string, string>>({});
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) {
      setDaten(demoAntwort(jahr));
      return;
    }
    setLaden(true);
    api
      .get<Antwort>(`/steuer/anlage-v?jahr=${jahr}`)
      .then((d) => {
        setDaten(d);
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Steuerübersicht konnte nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, [jahr]);

  const jahre = useMemo(() => [JETZT, JETZT - 1, JETZT - 2], []);
  const afaCent = (id: string) => parseEuroEingabe(afa[id] ?? '') ?? 0;
  const hatDaten = daten && daten.objekte.length > 0;

  const einkuenfteGesamt =
    daten?.objekte.reduce((s, o) => s + (o.ueberschussVorAfaCent - afaCent(o.objektId)), 0) ?? 0;

  return (
    <div>
      <Seitenkopf
        titel="Steuer — Anlage V"
        beschreibung="Vorbereitungshilfe für die Anlage V (Einkünfte aus Vermietung und Verpachtung) je Objekt: Einnahmen, Werbungskosten und Überschuss. Die Abschreibung (AfA) tragen Sie je Objekt selbst ein."
      />

      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm font-medium text-fg-muted">Jahr</span>
        <div className="flex gap-1 rounded-lg border border-border bg-surface p-1">
          {jahre.map((j) => (
            <button
              key={j}
              onClick={() => setJahr(j)}
              className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                j === jahr ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'
              }`}
            >
              {j}
            </button>
          ))}
        </div>
      </div>

      {fehler && !daten && (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      )}

      {laden && !daten ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : !hatDaten ? (
        <EmptyState
          icon={<IconSteuer groesse={22} />}
          titel="Noch keine Daten für die Anlage V"
          beschreibung="Sobald Objekte mit Mietverhältnissen und Belegen erfasst sind, erscheint hier die Aufstellung für die Steuererklärung."
        />
      ) : (
        <div className="space-y-6">
          <Card>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kennzahl label="Einnahmen gesamt" wert={formatEuro(daten!.summe.einnahmenGesamtCent)} />
              <Kennzahl label="Werbungskosten" wert={formatEuro(daten!.summe.werbungskostenCent)} />
              <Kennzahl label="Überschuss vor AfA" wert={formatEuro(daten!.summe.ueberschussVorAfaCent)} />
              <Kennzahl
                label="Einkünfte nach AfA"
                wert={formatEuro(einkuenfteGesamt)}
                farbe={einkuenfteGesamt >= 0 ? 'accent' : 'danger'}
              />
            </div>
          </Card>

          {daten!.objekte.map((o) => {
            const einkuenfte = o.ueberschussVorAfaCent - afaCent(o.objektId);
            return (
              <Card key={o.objektId} titel={o.bezeichnung}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-subtle">Einnahmen</h3>
                    <Zeile label="Kaltmiete (Jahressoll)" wert={formatEuro(o.einnahmenKaltmieteCent)} />
                    <Zeile label="Umlagen (Vorauszahlungen)" wert={formatEuro(o.einnahmenUmlagenCent)} />
                    <Zeile label="Einnahmen gesamt" wert={formatEuro(o.einnahmenGesamtCent)} betont />
                  </div>
                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-subtle">Werbungskosten</h3>
                    {o.positionen.length === 0 ? (
                      <p className="text-sm text-fg-muted">Keine Belege im Jahr {daten!.jahr}.</p>
                    ) : (
                      o.positionen.map((p, i) => <Zeile key={i} label={p.bezeichnung} wert={formatEuro(p.betragCent)} />)
                    )}
                    <Zeile label="Werbungskosten gesamt" wert={formatEuro(o.werbungskostenCent)} betont />
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
                  <label className="flex items-center gap-2 text-sm text-fg">
                    <span className="text-fg-muted">Gebäude-AfA / Jahr (€)</span>
                    <input
                      className="w-32 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent"
                      inputMode="decimal"
                      placeholder="z. B. 2 % vom Gebäudewert"
                      value={afa[o.objektId] ?? ''}
                      onChange={(e) => setAfa((a) => ({ ...a, [o.objektId]: e.target.value }))}
                    />
                  </label>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide text-fg-subtle">Einkünfte (Anlage V)</p>
                    <p className={`tabellenziffern text-lg font-bold ${einkuenfte >= 0 ? 'text-fg' : 'text-danger'}`}>
                      {formatEuro(einkuenfte)}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}

          <p className="px-1 text-xs text-fg-subtle">
            Vorbereitungshilfe, keine Steuerberatung. Einnahmen als Jahressoll aus den laufenden
            Mietverhältnissen; tatsächlich zu- und abgeflossene Beträge (Zufluss-/Abflussprinzip)
            können abweichen. Die AfA und weitere persönliche Positionen ergänzt Ihr Steuerberater.
          </p>
        </div>
      )}
    </div>
  );
}

function Kennzahl({ label, wert, farbe }: { label: string; wert: string; farbe?: 'accent' | 'danger' }) {
  const klasse = farbe === 'accent' ? 'text-accent' : farbe === 'danger' ? 'text-danger' : 'text-fg';
  return (
    <div className="rounded-lg border border-border bg-surface-2 p-3">
      <p className="text-xs text-fg-muted">{label}</p>
      <p className={`mt-1 tabellenziffern text-lg font-semibold ${klasse}`}>{wert}</p>
    </div>
  );
}

function Zeile({ label, wert, betont }: { label: string; wert: string; betont?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-sm text-fg-muted">{label}</span>
      <span className={`tabellenziffern text-sm ${betont ? 'font-semibold text-fg' : 'text-fg'}`}>{wert}</span>
    </div>
  );
}

export default Steuer;
